/**
 * DEFCAT P6 — Trees tab ★. Dual treeview (Location + Type) + a detail panel for
 * the selected node. Add child at the right level, rename, retire, move
 * (re-parent). Edits land on the DRAFT version; published versions are
 * read-only (surfaced inline).
 */
import { useMemo, useState, type ReactElement } from "react";
import { Box, Paper } from "@mui/material";
import type {
  CatalogueAxis,
  DefectCatalogueEntryV2,
  DefectClassModel,
  GetDefectCatalogueV2Response,
} from "@aeriosiq/types";
import { TreePanel, type ReorderEvent } from "./TreePanel.js";
import { EntryDetailPanel } from "./EntryDetailPanel.js";
import { L0DetailPanel } from "./L0DetailPanel.js";
import { AddEntryDialog } from "./AddEntryDialog.js";
import {
  buildAxisTree,
  buildLocationTreeWithL0,
  descendantIds,
  isL0NodeId,
  l0GroupOfLocationEntry,
  type TreeNode,
} from "./treeBuild.js";
import { deriveAxisLevels } from "./structureDerive.js";
import { addButtonLabel } from "./addLabels.js";
import { useComponentGroupNameResolver } from "./componentGroups.js";
import { locText } from "./locText.js";
import { useReorderEntriesMutation } from "./taxonomyQueries.js";

export interface TreesTabProps {
  catalogueId: string;
  versionId: string;
  structure: GetDefectCatalogueV2Response;
  editable: boolean;
  /** Catalogue class model — drives the TYPE-node default-severity dropdown. */
  classModel?: DefectClassModel | null | undefined;
}

interface AddTarget {
  axis: CatalogueAxis;
  parentEntryId: string | null;
  /**
   * DEFCAT-R2-04 — when the add was triggered under an L0 reference root, the
   * new ROOT location entry inherits the L0's componentGroupId (so it slots
   * back under that L0 root). parentEntryId stays null in that case.
   */
  componentGroupId?: string | null;
}

/**
 * DEFCAT-R2-09 — given a reorder event, compute the contiguous renumbering of
 * the affected sibling group and return only the entries whose sortOrder
 * actually changes (so we PATCH the minimum). Pure + unit-tested.
 */
export function computeReorderPatches(
  e: ReorderEvent,
): { entryId: string; sortOrder: number }[] {
  const ids = [...e.siblingIds];
  if (e.from < 0 || e.from >= ids.length || e.to < 0 || e.to >= ids.length) {
    return [];
  }
  const [moved] = ids.splice(e.from, 1);
  if (moved === undefined) return [];
  ids.splice(e.to, 0, moved);
  // Renumber 0..n-1; emit a patch only where the position changed.
  const patches: { entryId: string; sortOrder: number }[] = [];
  ids.forEach((entryId, idx) => {
    if (e.siblingIds[idx] !== entryId)
      patches.push({ entryId, sortOrder: idx });
  });
  return patches;
}

export function TreesTab({
  catalogueId,
  versionId,
  structure,
  editable,
  classModel,
}: TreesTabProps): ReactElement {
  const entries = structure.entries;
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [addTarget, setAddTarget] = useState<AddTarget | null>(null);

  // DEFCAT-R2-04 — resolve L0 component-group display names from assets/v1.
  const groupName = useComponentGroupNameResolver();

  // DEFCAT-R2-04 — L0-ROOTED location tree; type tree unchanged.
  const locationTree = useMemo(
    () => buildLocationTreeWithL0(entries, groupName),
    [entries, groupName],
  );
  const typeTree = useMemo(() => buildAxisTree(entries, "type"), [entries]);

  // DEFCAT-R2-15/R2-11 — derived structure levels feed the add-button labels.
  const derivedLevels = useMemo(
    () => deriveAxisLevels(entries, structure.axisLevels),
    [entries, structure.axisLevels],
  );

  // Catalogue depth of an entry (for the level-aware add label). L0 synthetic
  // roots have no catalogue depth — adding under them is a root add (null).
  const depthOf = useMemo(() => {
    const byId = new Map<string, DefectCatalogueEntryV2>();
    for (const e of entries) byId.set(e.entryId, e);
    return (entryId: string): number => {
      let d = 0;
      const seen = new Set<string>();
      let cur = byId.get(entryId);
      while (cur && !seen.has(cur.entryId)) {
        seen.add(cur.entryId);
        const pid = cur.parentEntryId;
        const parent =
          pid != null && pid.length > 0 ? byId.get(pid) : undefined;
        if (!parent) break;
        d += 1;
        cur = parent;
      }
      return d;
    };
  }, [entries]);

  const addLabelFor = useMemo(
    () =>
      (axis: CatalogueAxis) =>
      (parent: TreeNode | null): string => {
        // Under an L0 root (or no parent) → root add (catalogue depth null).
        const depth =
          parent && !isL0NodeId(parent.entry.entryId)
            ? depthOf(parent.entry.entryId)
            : null;
        return addButtonLabel(axis, depth, derivedLevels);
      },
    [depthOf, derivedLevels],
  );

  const selectedEntry: DefectCatalogueEntryV2 | null = useMemo(
    () =>
      selectedId !== null && !isL0NodeId(selectedId)
        ? (entries.find((e) => e.entryId === selectedId) ?? null)
        : null,
    [entries, selectedId],
  );

  // The selected L0 reference node (read-only detail), if any.
  const selectedL0: TreeNode | null = useMemo(() => {
    if (selectedId === null || !isL0NodeId(selectedId)) return null;
    return locationTree.find((n) => n.entry.entryId === selectedId) ?? null;
  }, [selectedId, locationTree]);

  const reorder = useReorderEntriesMutation(catalogueId, versionId);

  function handleReorder(e: ReorderEvent): void {
    // SYNCHRONOUS optimistic write + batch persistence — the cache re-sort
    // lands in the SAME React commit as dnd-kit's drag-end, so neither the
    // dragged row nor the displaced siblings move twice.
    reorder.reorderNow(computeReorderPatches(e));
  }

  // Parent options for the detail panel: same axis, excluding self +
  // descendants (can't re-parent under your own subtree) and excluding the
  // synthetic L0 roots (re-parenting under an L0 = clearing the entry's own
  // parent, which the "(root)" option already covers).
  const parentOptions = useMemo(() => {
    if (!selectedEntry) return [];
    const axisTree =
      selectedEntry.axis === "location" ? locationTree : typeTree;
    const blocked = new Set<string>();
    const stack = [...axisTree];
    while (stack.length > 0) {
      const n = stack.pop();
      if (!n) continue;
      if (n.entry.entryId === selectedEntry.entryId) {
        for (const id of descendantIds(n)) blocked.add(id);
      }
      stack.push(...n.children);
    }
    return entries
      .filter((e) => e.axis === selectedEntry.axis && !blocked.has(e.entryId))
      .map((e) => ({ entryId: e.entryId, label: locText(e.name) }));
  }, [selectedEntry, entries, locationTree, typeTree]);

  // DEFCAT-R2-04 AC4 — the parent display for the selected entry's detail
  // panel. Real catalogue parent → its name; depth-0 location entry → its L0
  // component-group name (it has no catalogue parent but hangs under the L0).
  const parentDisplay = useMemo<string | null>(() => {
    if (!selectedEntry) return null;
    const pid = selectedEntry.parentEntryId;
    if (pid != null && pid.length > 0) {
      const parent = entries.find((e) => e.entryId === pid);
      return parent ? locText(parent.name) : "";
    }
    if (selectedEntry.axis === "location") {
      const byId = new Map<string, DefectCatalogueEntryV2>();
      for (const e of entries)
        if (e.axis === "location") byId.set(e.entryId, e);
      const cg = l0GroupOfLocationEntry(selectedEntry, byId);
      if (cg !== null) {
        const n = groupName(cg);
        return n != null && n.length > 0 ? n : "(component group)";
      }
      return "(no component group)";
    }
    return "";
  }, [selectedEntry, entries, groupName]);

  // DEFCAT-R2-04 — add under an L0 reference root: the new ROOT location entry
  // inherits the L0's componentGroupId (parentEntryId stays null). Adding under
  // a real entry is an ordinary child add.
  function handleAddLocation(parentId: string | null): void {
    if (parentId !== null && isL0NodeId(parentId)) {
      const l0 = locationTree.find((n) => n.entry.entryId === parentId);
      setAddTarget({
        axis: "location",
        parentEntryId: null,
        componentGroupId: l0?.l0?.componentGroupId ?? null,
      });
      return;
    }
    setAddTarget({ axis: "location", parentEntryId: parentId });
  }

  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: { xs: "1fr", md: "1fr 1fr 360px" },
        gap: 2,
        minHeight: 0,
        height: "100%",
      }}
    >
      <Paper variant="outlined" sx={{ p: 1.5, minHeight: 0, display: "flex" }}>
        <TreePanel
          title="Location tree"
          nodes={locationTree}
          selectedId={selectedId}
          onSelect={setSelectedId}
          addRootLabel="Add location"
          addLabelFor={addLabelFor("location")}
          onAddChild={editable ? handleAddLocation : undefined}
          onReorder={editable ? handleReorder : undefined}
        />
      </Paper>
      <Paper variant="outlined" sx={{ p: 1.5, minHeight: 0, display: "flex" }}>
        <TreePanel
          title="Type tree"
          nodes={typeTree}
          selectedId={selectedId}
          onSelect={setSelectedId}
          addRootLabel="Add type"
          addLabelFor={addLabelFor("type")}
          onAddChild={
            editable
              ? (parentEntryId) => {
                  setAddTarget({ axis: "type", parentEntryId });
                }
              : undefined
          }
          onReorder={editable ? handleReorder : undefined}
        />
      </Paper>
      <Paper variant="outlined" sx={{ minHeight: 0, overflowY: "auto" }}>
        {selectedL0 ? (
          <L0DetailPanel node={selectedL0} />
        ) : selectedEntry ? (
          <EntryDetailPanel
            catalogueId={catalogueId}
            versionId={versionId}
            entry={selectedEntry}
            parentOptions={parentOptions}
            parentDisplay={parentDisplay}
            editable={editable}
            classModel={classModel}
          />
        ) : (
          <Box sx={{ p: 2, color: "text.secondary", fontSize: 14 }}>
            Select a node to edit its details.
          </Box>
        )}
      </Paper>

      {addTarget ? (
        <AddEntryDialog
          open
          catalogueId={catalogueId}
          versionId={versionId}
          axis={addTarget.axis}
          parentEntryId={addTarget.parentEntryId}
          componentGroupId={addTarget.componentGroupId ?? null}
          levelLabel={addButtonLabel(
            addTarget.axis,
            addTarget.parentEntryId !== null
              ? depthOf(addTarget.parentEntryId)
              : null,
            derivedLevels,
          )
            .replace(/^Add\s+/i, "")
            .trim()}
          onClose={() => {
            setAddTarget(null);
          }}
        />
      ) : null}
    </Box>
  );
}
